import { SIBLING_TYPE, TWIN_TYPE } from './config';

// How a multiple pregnancy's recorded zygosity becomes twin fields — the SINGLE definition, shared by
// the two places that need it so they cannot drift:
//   * deriveReproNodes, stamping the derived FETUS nodes so a multiple draws as a twin group;
//   * the live-birth conversion, stamping the real CHILDREN those fetuses become (decision 18).
// Without the second, converting both twins would silently produce two ordinary siblings and the twin
// relationship recorded during the pregnancy would be lost at the moment of birth.

export const ZYGOSITY_TO_TWIN_TYPE = Object.freeze({
  monozygotic: TWIN_TYPE.monozygotic,
  dizygotic: TWIN_TYPE.dizygotic,
  unknown: TWIN_TYPE.unknown
});

export const SIBLING_TYPE_BY_COUNT = Object.freeze({
  2: SIBLING_TYPE.twin,
  3: SIBLING_TYPE.triplet,
  4: SIBLING_TYPE.quadruplet
});

// The twin descriptors for a multiple of `count` siblings with this recorded zygosity. An unrecorded
// zygosity is `unknown`, which Bennett draws as a "?" between the twin lines — the honest default, and
// the convention Tim ruled prevails over Desktop's plain slanted lines.
export const twinFieldsFor = ({ count, zygosity }) => ({
  siblingType: SIBLING_TYPE_BY_COUNT[count] || SIBLING_TYPE.quadruplet,
  twinType: ZYGOSITY_TO_TWIN_TYPE[zygosity] ?? TWIN_TYPE.unknown
});

// A stable-enough group id. Deliberately not derived from the pregnancy `_id`: a carrier can have two
// separate twin pregnancies, and they must not share a twin group.
export const newTwinGroupId = () =>
  `twin-group-${Math.random().toString(36).slice(2, 8)}`;

// Stamp twin membership symmetrically across a set of member pids. Mirrors what addSiblingsToRecords
// does for a twin added by hand: a shared `twinSubGroupId`, the `siblingType`, and `twinRelations`
// naming every OTHER member of the group. `siblingIds` are deliberately untouched — the children of one
// pregnancy already share both parents, so the ordinary sibling edges are maintained by
// addChildrenToRecords, and rewriting them here could clobber half-siblings.
//
// An existing group id among the members wins, so converting a third fetus joins the group the first
// two already formed rather than starting a rival one.
export const linkTwinMembers = (members, pids, { siblingType, twinType }) => {
  const inGroup = (pids || []).map(String);
  if (inGroup.length < 2) return members || [];
  const list = members || [];
  const existingGroupId = list
    .filter((m) => inGroup.includes(String(m.pid)) && m.twinSubGroupId)
    .map((m) => m.twinSubGroupId)[0];
  const groupId = existingGroupId || newTwinGroupId();

  return list.map((m) => {
    if (!inGroup.includes(String(m.pid))) return m;
    const others = inGroup.filter((p) => p !== String(m.pid));
    // Preserve any twin relation to someone OUTSIDE this group (a pre-existing twin), then assert the
    // relation to each member of it.
    const kept = (m.twinRelations || []).filter(
      (r) => !others.includes(String(r?.withPid))
    );
    return {
      ...m,
      twinSubGroupId: groupId,
      siblingType,
      isTwin: siblingType === SIBLING_TYPE.twin,
      twinRelations: [
        ...kept,
        ...others.map((withPid) => ({
          // Numeric where the pid is numeric, matching how the rest of the graph stores it.
          withPid: Number.isNaN(Number(withPid)) ? withPid : Number(withPid),
          type: twinType
        }))
      ]
    };
  });
};
